/**
 * Core API — posts domain (`feed_posts`, `post_by_id`, …).
 */

/** What a post card or a story needs to show a business page instead of its author. */
export const CORE_PAGE_BADGE_FIELDS = "page { id username name logoUrl isVerified }";
/** Poll results as the server computes them; present when the post is a poll. */
export const CORE_POST_POLL_FIELDS = "poll { id postId endsAtText isClosed totalVotes myOptionId options { id text votes percent } }";
/** Battle results as the server computes them; present when the post is a battle. */
export const CORE_POST_BATTLE_FIELDS =
    "battle { id postId kind status canAnswer durationHours endsAtText isClosed allowVoteChange totalVotes mySideId leaderSideId winnerSideId isTie " +
    "opponent { userId username fullName avatarUrl } sides { id label imageUrl videoUrl ownerId votes percent " +
    "team { teamId name logoUrl memberCount viewerIsMember viewerIsLeader } } }";

/** A PDF's own fields on a media node (`mediaType` DOCUMENT; its `url` is the cover page). */
export const CORE_POST_DOCUMENT_FIELDS = "title pageCount allowDownload downloadUrl pages { number url width height }";

/**
 * One-level original post for reshared cards.
 * Do not nest `sharedFrom { sharedFrom { ... } }` (avoids recursive payloads).
 */
export const CORE_POST_SHARED_FROM_PREVIEW = `
          sharedFrom {
            id
            text
            code
            createdAtText
            sharesCount
            clones
            sharedByMe
            myShareId
            ${CORE_PAGE_BADGE_FIELDS}
            author {
              userId
              username
              fullName
              profile {
                avatarUrl
                isOnline
                lastSeenText
              }
            }
            media(first: 10) {
              edges {
                node {
                  url
                  thumbnailUrl
                  mediaType
                  ${CORE_POST_DOCUMENT_FIELDS}
                }
              }
            }
          }
`;

/** Author block of a post card: avatar, sticker badge, verified tick and presence, or the page it was posted as. */
export const CORE_POST_AUTHOR_FIELDS =
    `author { userId username fullName profile { avatarUrl sticker isVerified isOnline lastSeenText } } ${CORE_PAGE_BADGE_FIELDS}`;
/** Where a post shared with people nearby sits, and who said "I'm in". */
export const CORE_POST_NEARBY_FIELDS =
    "nearby { placeName distanceKm expiresAtText isLive onMap joinedCount joinedByMe }";
/** The place a post was written about ("at Sun Club"), with its page link. */
export const CORE_POST_PLACE_FIELDS = "place { id slug name kind city latitude longitude url postsCount }";
/** Who the author was with, and where each is on a photo (`mediaIndex`, `x`/`y` as 0–1 of its size). */
export const CORE_POST_TAGS_FIELDS =
    "tags { user { userId username fullName profile { avatarUrl } } mediaIndex x y status }";
/** Card extras: the follow pill, the best comment previewed under the post, the nearby strip, and its place and people. */
export const CORE_POST_CARD_EXTRAS =
    "followsAuthor topComment { id text isPinned createdAtText reactionsCount topReactions " +
    `user { userId username fullName profile { avatarUrl } } } ${CORE_POST_NEARBY_FIELDS} ${CORE_POST_PLACE_FIELDS} ${CORE_POST_TAGS_FIELDS}`;

/** Home feed — replaces website `engagingFeed` / REST `mainPosts/`. */
export const CORE_QUERY_FEED_POSTS = `
  query CoreFeedPosts($first: Int!, $after: String) {
    me {
      userId
    }
    feedPosts(first: $first, after: $after) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        cursor
        node {
          id
          text
          code
          visibility
          background
          layout
          ${CORE_POST_POLL_FIELDS}
          ${CORE_POST_BATTLE_FIELDS}
          isEdited
          isPinned
          authorId
          teamId
          sharedFromId
          rootSharedFromId
          sharesCount
          clones
          sharedByMe
          myShareId
          createdAtText
          commentsCount
          reactionsCount
          savedByMe
          reactedByMe
          myReaction
          topReactions
          reactorName
          ${CORE_POST_AUTHOR_FIELDS}
          ${CORE_POST_CARD_EXTRAS}
          media(first: 50) {
            edges {
              node {
                url
                thumbnailUrl
                mediaType
                ${CORE_POST_DOCUMENT_FIELDS}
              }
            }
          }
${CORE_POST_SHARED_FROM_PREVIEW}
        }
      }
    }
  }
`;

/** Single post — use with mapper alongside profile-specific flows. */
export const CORE_QUERY_POST_BY_ID = `
  query CorePostById($postId: Int!) {
    me {
      userId
    }
    postById(postId: $postId) {
      id
      text
      code
      visibility
      background
      layout
      ${CORE_POST_POLL_FIELDS}
      ${CORE_POST_BATTLE_FIELDS}
      isEdited
      isPinned
      authorId
      teamId
      sharedFromId
      rootSharedFromId
      sharesCount
      sharedByMe
      createdAtText
      commentsCount
      reactionsCount
      savedByMe
      reactedByMe
      myReaction
      topReactions
      reactorName
      ${CORE_POST_AUTHOR_FIELDS}
      ${CORE_POST_CARD_EXTRAS}
      media(first: 50) {
        edges {
          node {
            url
            thumbnailUrl
            mediaType
            ${CORE_POST_DOCUMENT_FIELDS}
          }
        }
      }
${CORE_POST_SHARED_FROM_PREVIEW}
    }
  }
`;

/** Saved posts — `SavedPost` nodes wrap the underlying post card fields. */
export const CORE_QUERY_MY_SAVED_POSTS = `
  query CoreMySavedPosts($first: Int!, $after: String) {
    me {
      userId
    }
    mySavedPosts(first: $first, after: $after) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        cursor
        node {
          id
          post {
            id
            text
            code
            visibility
            background
            layout
            ${CORE_POST_POLL_FIELDS}
            ${CORE_POST_BATTLE_FIELDS}
            isEdited
            isPinned
            authorId
            teamId
            sharedFromId
            rootSharedFromId
            sharesCount
            sharedByMe
            createdAtText
            commentsCount
            reactionsCount
            savedByMe
            reactedByMe
            myReaction
            topReactions
            reactorName
            ${CORE_POST_AUTHOR_FIELDS}
            ${CORE_POST_CARD_EXTRAS}
            media(first: 50) {
              edges {
                node {
                  url
                  thumbnailUrl
                  mediaType
                  ${CORE_POST_DOCUMENT_FIELDS}
                }
              }
            }
${CORE_POST_SHARED_FROM_PREVIEW}
          }
        }
      }
    }
  }
`;

/** Posts tagged with a hashtag — replaces legacy `postsByTrend`. */
export const CORE_QUERY_POSTS_BY_HASHTAG = `
  query CorePostsByHashtag($hashtag: String!, $first: Int!, $after: String) {
    me {
      userId
    }
    postsByHashtag(hashtag: $hashtag, first: $first, after: $after) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        cursor
        node {
          id
          text
          code
          visibility
          background
          layout
          ${CORE_POST_POLL_FIELDS}
          ${CORE_POST_BATTLE_FIELDS}
          isEdited
          isPinned
          authorId
          teamId
          sharedFromId
          rootSharedFromId
          sharesCount
          clones
          sharedByMe
          myShareId
          createdAtText
          commentsCount
          reactionsCount
          savedByMe
          reactedByMe
          myReaction
          topReactions
          reactorName
          ${CORE_POST_AUTHOR_FIELDS}
          ${CORE_POST_CARD_EXTRAS}
          media(first: 50) {
            edges {
              node {
                url
                thumbnailUrl
                mediaType
                ${CORE_POST_DOCUMENT_FIELDS}
              }
            }
          }
${CORE_POST_SHARED_FROM_PREVIEW}
        }
      }
    }
  }
`;

/** At most one pinned post for a profile (filter `isPinned: true`). */
export const CORE_QUERY_PINNED_USER_POST = `
  query CorePinnedUserPost($username: String!) {
    me {
      userId
    }
    userPosts(username: $username, first: 1, isPinned: true) {
      edges {
        node {
          id
          text
          code
          visibility
          background
          layout
          ${CORE_POST_POLL_FIELDS}
          ${CORE_POST_BATTLE_FIELDS}
          isEdited
          isPinned
          authorId
          teamId
          sharedFromId
          rootSharedFromId
          sharesCount
          clones
          sharedByMe
          myShareId
          createdAtText
          commentsCount
          reactionsCount
          savedByMe
          reactedByMe
          myReaction
          topReactions
          reactorName
          ${CORE_POST_AUTHOR_FIELDS}
          ${CORE_POST_CARD_EXTRAS}
          media(first: 50) {
            edges {
              node {
                url
                thumbnailUrl
                mediaType
                ${CORE_POST_DOCUMENT_FIELDS}
              }
            }
          }
${CORE_POST_SHARED_FROM_PREVIEW}
        }
      }
    }
  }
`;

/** Profile grid — replaces legacy `user_posts` website shape when migrated. */
/** Post node selection shared by the profile and team feeds (`coreFeedPostNodeToRest`). */
export const CORE_FEED_POST_NODE_FIELDS = `
          id
          text
          code
          visibility
          background
          layout
          ${CORE_POST_POLL_FIELDS}
          ${CORE_POST_BATTLE_FIELDS}
          isEdited
          isPinned
          authorId
          teamId
          sharedFromId
          rootSharedFromId
          sharesCount
          clones
          sharedByMe
          myShareId
          createdAtText
          commentsCount
          reactionsCount
          savedByMe
          reactedByMe
          myReaction
          topReactions
          reactorName
          ${CORE_POST_AUTHOR_FIELDS}
          ${CORE_POST_CARD_EXTRAS}
          media(first: 50) {
            edges {
              node {
                url
                thumbnailUrl
                mediaType
                ${CORE_POST_DOCUMENT_FIELDS}
              }
            }
          }
${CORE_POST_SHARED_FROM_PREVIEW}
`;

export const CORE_QUERY_USER_POSTS = `
  query CoreUserPosts($username: String!, $first: Int!, $after: String) {
    me {
      userId
    }
    userPosts(username: $username, first: $first, after: $after) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
${CORE_FEED_POST_NODE_FIELDS}
        }
      }
    }
  }
`;

/** "Around you": live posts shared with people near the viewer (`sort`: newest, closest or popular). */
export const CORE_QUERY_NEARBY_POSTS = `
  query CoreNearbyPosts($sort: String, $radiusKm: Int, $offset: Int) {
    me {
      userId
    }
    nearbyPosts(sort: $sort, radiusKm: $radiusKm, offset: $offset) {
${CORE_FEED_POST_NODE_FIELDS}
    }
  }
`;

/** The video feed: others' original posts that carry a video. */
export const CORE_QUERY_VIDEO_POSTS = `
  query CoreVideoPosts($first: Int!, $after: String) {
    me {
      userId
    }
    videoPosts(first: $first, after: $after) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
${CORE_FEED_POST_NODE_FIELDS}
        }
      }
    }
  }
`;

export const CORE_QUERY_TEAM_POSTS = `
  query CoreTeamPosts($teamId: Int!, $first: Int!, $after: String) {
    me {
      userId
    }
    teamPosts(teamId: $teamId, first: $first, after: $after) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
${CORE_FEED_POST_NODE_FIELDS}
        }
      }
    }
  }
`;

/** People who reacted to a comment (legacy `likeComments/` REST). */
export const CORE_QUERY_COMMENT_REACTIONS = `
  query CoreCommentReactions($commentId: Int!, $first: Int!, $after: String) {
    commentReactions(commentId: $commentId, first: $first, after: $after) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          reaction
          user {
            username
            fullName
            profile {
              avatarUrl
            }
          }
        }
      }
    }
  }
`;

/** Who reacted to a post, optionally one reaction only; `withCounts` adds the per-reaction tabs. */
export const CORE_QUERY_POST_REACTIONS = `
  query CorePostReactions($postId: Int!, $reaction: String, $first: Int!, $after: String, $withCounts: Boolean!) {
    postById(postId: $postId) @include(if: $withCounts) {
      id
      reactionsCount
      reactionCounts {
        reaction
        count
      }
    }
    postReactions(postId: $postId, reaction: $reaction, first: $first, after: $after) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          reaction
          user {
            userId
            username
            fullName
            isFriend
            youFollow
            friendRequestSent
            profile {
              avatarUrl
            }
          }
        }
      }
    }
  }
`;

/** A public post for people who aren't signed in (meetoor.com/p/<id>), its top comments and more by its author. */
export const CORE_QUERY_PUBLIC_POST = `
  query CorePublicPost($postId: Int!) {
    publicPost(postId: $postId) {
      id
      text
      code
      visibility
      background
      layout
      createdAt
      searchIndexable
      ${CORE_POST_POLL_FIELDS}
      ${CORE_POST_BATTLE_FIELDS}
      isEdited
      isPinned
      authorId
      teamId
      sharedFromId
      rootSharedFromId
      sharesCount
      sharedByMe
      createdAtText
      commentsCount
      reactionsCount
      savedByMe
      reactedByMe
      myReaction
      topReactions
      reactorName
      ${CORE_POST_AUTHOR_FIELDS}
      ${CORE_POST_CARD_EXTRAS}
      media(first: 50) {
        edges {
          node {
            url
            thumbnailUrl
            mediaType
            ${CORE_POST_DOCUMENT_FIELDS}
          }
        }
      }
      ${CORE_POST_SHARED_FROM_PREVIEW}
    }
    publicPostComments(postId: $postId, first: 3) {
      id
      text
      isPinned
      createdAtText
      reactionsCount
      repliesCount
      user { userId username fullName profile { avatarUrl isVerified } }
    }
    publicAuthorPosts(postId: $postId, first: 3) {
      id
      text
      createdAtText
      commentsCount
      reactionsCount
      media(first: 1) { edges { node { url thumbnailUrl mediaType } } }
    }
  }
`;

export const CORE_QUERY_PUBLIC_USER_POSTS = `
  query CorePublicUserPosts($username: String!, $first: Int, $beforeId: Int) {
    publicUserPosts(username: $username, first: $first, beforeId: $beforeId) {
      id
      text
      code
      visibility
      background
      layout
      createdAt
      searchIndexable
      ${CORE_POST_POLL_FIELDS}
      ${CORE_POST_BATTLE_FIELDS}
      isEdited
      isPinned
      authorId
      teamId
      sharedFromId
      rootSharedFromId
      sharesCount
      sharedByMe
      createdAtText
      commentsCount
      reactionsCount
      savedByMe
      reactedByMe
      myReaction
      topReactions
      reactorName
      ${CORE_POST_AUTHOR_FIELDS}
      ${CORE_POST_CARD_EXTRAS}
      media(first: 50) {
        edges {
          node {
            url
            thumbnailUrl
            mediaType
            ${CORE_POST_DOCUMENT_FIELDS}
          }
        }
      }
      ${CORE_POST_SHARED_FROM_PREVIEW}
    }
  }
`;
